import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { useLocation, useNavigate } from 'react-router-dom';
import { setSelectedRoute } from '../../app/menu/menuStateSlice.js';
import NestedListItem from './NestedListItem.jsx';
// Update the import to match the actual exports from menuItems.jsx
import {
  mainListItems,
  secondaryListItems,
} from './navigationData/menuItems.jsx';
// Remove WorkflowListItems if not used or import it correctly if needed
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getEnabledMenuItems } from '../../pages/utilities.js';
import './navigationCss/cssOverride.css';
import { useGetApiApprovalQueueQuery } from '../../app/athenaApiGen.js';

const MenuContent = () => {
  // State to keep track of the selected index
  // Initialize selectedIndex to null or the index of the default selected item
  // If you want to set a default selected item, you can initialize it here
  // For example, to set the first item as selected by default:
  // const [selectedIndex, setSelectedIndex] = useState(0); // Default to first item
  const { selectedRoute } = useSelector((state) => state.menu);
  const { actions, availableForms } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const enabledItems = getEnabledMenuItems(actions, availableForms);

  const { data: pendingData } = useGetApiApprovalQueueQuery({
    page: 1,
    limit: 1,
    approvalState: 'pending',
  });
  const pendingCount = pendingData?.totalCount ?? 0;

  const allowedMenuItems = useMemo(() => {
    return mainListItems.map((item) => {
      const isEnabled =
        (item.text === 'Approvals' && enabledItems.approvalQueue) ||
        (item.text === 'Home' && enabledItems.home) ||
        (item.text === 'Projects' && enabledItems.projects) ||
        (item.text === 'Deliverables' && enabledItems.deliverables) ||
        (item.text === 'Financials' && enabledItems.financials) ||
        (item.text === 'Schedule' && enabledItems.ims) ||
        (item.text === 'Library' && enabledItems.library) ||
        (item.text === 'Forms' &&
          (enabledItems.forms.ctp ||
            enabledItems.forms.pnr ||
            enabledItems.forms.rip ||
            enabledItems.forms.tar ||
            enabledItems.forms.tripReport));

      if (item.text === 'Forms' && isEnabled) {
        const filteredChildren = item.children.filter((child) => {
          if (child.text === 'CTP') return enabledItems.forms.ctp;
          if (child.text === 'PNR') return enabledItems.forms.pnr;
          if (child.text === 'RIP') return enabledItems.forms.rip;
          if (child.text === 'TAR') return enabledItems.forms.tar;
          if (child.text === 'Trip Report') return enabledItems.forms.tr;
          return false;
        });
        return { ...item, children: filteredChildren };
      }

      return isEnabled && item;
    });
  }, [enabledItems]);

  useEffect(() => {
    const pathname = location.pathname || '/';

    // Build a set of known paths from all menu items, including children and secondary
    const allTop = [...allowedMenuItems, ...secondaryListItems];
    const allChildren = allowedMenuItems.flatMap((mi) =>
      Array.isArray(mi?.children) ? mi.children : [],
    );

    // Admin is no longer in the drawer — reached via the header dropdown.
    const allMenuItems = [...allTop, ...allChildren];
    const knownPaths = new Set(allMenuItems.map((i) => i?.path));

    // Normalize arbitrary nested paths to the nearest known path by stripping segments from the end
    const normalizeToKnownPath = (p) => {
      if (knownPaths.has(p)) return p;

      // Check if this is a form path that belongs to a list (e.g., /pnr matches /pnr-list)
      const formMatch = allMenuItems.find((item) => {
        if (!item) return false;
        if (!item.path.includes('-list')) return false;
        const basePath = item.path.replace('-list', '');
        // Use regex to ensure exact match of base path
        const pattern = new RegExp(`^${basePath.replace(/\//g, '\\/')}($|/)`);
        return pattern.test(p);
      });
      if (formMatch) return formMatch.path;

      // Strip trailing slash for consistency
      let segs = p.replace(/\/$/, '').split('/');
      while (segs.length > 1) {
        segs = segs.slice(0, -1);
        const candidate = segs.join('/') || '/';
        if (knownPaths.has(candidate)) return candidate;
      }
      return '/';
    };

    const normalizedPath = normalizeToKnownPath(pathname);
    const match = allMenuItems.find((i) => i?.path === normalizedPath);
    dispatch(setSelectedRoute(match?.id ?? 0));
  }, [location, dispatch, allowedMenuItems]);

  const handleListItemClick = (event, path, id) => {
    event.preventDefault();
    dispatch(setSelectedRoute(id));
    navigate(path);
  };

  return (
    <Stack
      sx={{
        flexGrow: 0,
        p: 1,
        justifyContent: 'space-between',
        overflow: 'hidden',
      }}
    >
      <List
        className=" custom-drawer MuiList-root"
        sx={{ padding: 0, overflow: 'auto' }}
      >
        {allowedMenuItems.map((item, index) => {
            if (!item) return null;

            if (!!item?.children && item?.children.length > 0) {
              const childrenIds = item.children.map((child) => child.id);
              return (
                <NestedListItem
                  key={item.id}
                  isSelected={childrenIds.includes(selectedRoute)}
                  item={item}
                >
                  <List>
                    {item.children.map((nestedChild) => (
                      <ListItem
                        className="MuiListItem-root .MuiButtonBase-root"
                        key={nestedChild.path}
                        sx={{ display: 'block' }}
                      >
                        <ListItemButton
                          selected={nestedChild.id === selectedRoute}
                          onClick={(event) =>
                            handleListItemClick(
                              event,
                              nestedChild.path,
                              nestedChild.id,
                            )
                          }
                          sx={{
                            '&.Mui-selected': {
                              borderLeft: '4px solid #1976d2',
                              borderLeftWidth: '4px',
                              '& .MuiListItemText-primary': {
                                fontWeight: 700,
                              },
                            },
                            '&.Mui-focusVisible': {
                              backgroundColor: '#1c8cfd',
                            },
                            ':hover': {
                              backgroundColor: '#4da6ff',
                            },
                          }}
                        >
                          <ListItemIcon className="MuiSvgIcon-root">
                            {nestedChild.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={nestedChild.text}
                            slotProps={{
                              primary: {
                                noWrap: true,
                                title: nestedChild.text,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </NestedListItem>
              );
            } else {
              return (
                <ListItem
                  key={index}
                  disablePadding
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ListItemButton
                    selected={item.id === selectedRoute}
                    onClick={(event) =>
                      handleListItemClick(event, item.path, item.id)
                    }
                    sx={{
                      justifyContent: 'space-between',
                      '&.Mui-selected': {
                        borderLeft: '4px solid #1976d2',
                        borderLeftWidth: '4px',
                        '& .MuiListItemText-primary': {
                          fontWeight: 700,
                        },
                      },
                      '&.Mui-focusVisible': {
                        backgroundColor: '#1c8cfd',
                      },
                      ':hover': {
                        backgroundColor: '#4da6ff',
                      },
                      '&:hover svg': {
                        color: '#154F85 !important', // Icon color on hover
                      }, // Ensure the icon uses the parent's color by default
                      '& svg': {
                        color: 'inherit',
                        transition: 'color 0.2s',
                      },
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      slotProps={{
                        primary: {
                          noWrap: true,
                          title: item.text,
                        },
                      }}
                    />

                    {item.id === 2 && pendingCount > 0 && (
                      <Box
                        sx={{
                          backgroundColor: '#F57820',
                          color: '#FFFFFF',
                          borderRadius: '10px',
                          padding: '2px 8px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          minWidth: '20px',
                          textAlign: 'center',
                        }}
                      >
                        {pendingCount}
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
              );
            }
          })}
      </List>
    </Stack>
  );
};

export default MenuContent;
